type CellMarks = {
  b?: boolean;
  i?: boolean;
  u?: boolean;
  link?: string;
};

type Cell = {
  value: string;
  type: 'th' | 'td';
  marks: CellMarks;
};

type CsvTableProps = {
  tableData?: string;
};

function CellContent({ cell }: { cell: Cell }) {
  let content: React.ReactNode = cell.value;

  if (cell.marks.b) content = <strong>{content}</strong>;
  if (cell.marks.i) content = <em>{content}</em>;
  if (cell.marks.u) content = <u>{content}</u>;
  if (cell.marks.link) {
    const isSafeUrl =
      /^https?:\/\//i.test(cell.marks.link) ||
      /^mailto:/i.test(cell.marks.link) ||
      /^tel:/i.test(cell.marks.link) ||
      cell.marks.link.startsWith('/');

    if (isSafeUrl) {
      content = (
        <a href={cell.marks.link} className="text-blue-700 underline hover:text-blue-900">
          {content}
        </a>
      );
    }
  }

  return <>{content}</>;
}

export const CsvTable: React.FC<CsvTableProps> = ({ tableData }) => {
  if (!tableData) return null;

  let table: Cell[][];
  try {
    table = JSON.parse(tableData);
  } catch {
    return null;
  }

  const isValidTable =
    Array.isArray(table) &&
    table.length > 0 &&
    table.every((row) => Array.isArray(row) && row.every((cell) => cell && typeof cell === 'object'));

  if (!isValidTable) return null;

  const hasHeaderRow = table[0].every((cell) => cell.type === 'th');
  const headerRow = hasHeaderRow ? table[0] : null;
  const bodyRows = hasHeaderRow ? table.slice(1) : table;

  return (
    <div className="overflow-x-auto py-5" role="region" aria-label="Data table">
      <table className="min-w-full border-collapse">
        {headerRow && (
          <thead>
            <tr>
              {headerRow.map((cell, colIndex) => (
                <th
                  key={colIndex}
                  scope="col"
                  className="border border-gray-200 bg-gray-100 px-4 py-3 text-left text-sm font-semibold text-gray-900"
                >
                  <CellContent cell={cell} />
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {bodyRows.map((row, rowIndex) => (
            <tr key={rowIndex} className="even:bg-gray-50">
              {row.map((cell, colIndex) => {
                const Tag = cell.type === 'th' ? 'th' : 'td';
                return (
                  <Tag
                    key={colIndex}
                    {...(cell.type === 'th' ? { scope: 'row' } : {})}
                    className={`border border-gray-200 px-4 py-3 text-sm text-gray-700 ${
                      cell.type === 'th' ? 'bg-gray-100 font-semibold text-gray-900' : ''
                    }`}
                  >
                    <CellContent cell={cell} />
                  </Tag>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
